import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'reset' | 'success'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devToken, setDevToken] = useState('');

  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  // Si on arrive avec un token dans l'URL, on passe directement à l'étape reset
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      setStep('reset');
    }
  }, [searchParams]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        // En dev/preview sans SMTP: le backend renvoie dev_token → on saute directement à l'étape reset
        if (data.dev_token) {
          setDevToken(data.dev_token);
          setToken(data.dev_token);
          setStep('reset');
        } else {
          // En production: l'email a été envoyé, on confirme à l'utilisateur
          setStep('sent');
        }
      } else {
        setError(data.detail || L('Une erreur est survenue.', 'An error occurred.'));
      }
    } catch (err) {
      setError(L('Erreur de connexion au serveur.', 'Server connection error.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError(L('Le mot de passe doit contenir au moins 6 caractères.', 'Password must be at least 6 characters.'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(L('Les mots de passe ne correspondent pas.', 'Passwords do not match.'));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        setStep('success');
        setTimeout(() => navigate('/espace-client'), 3000);
      } else {
        setError(data.detail || L('Token invalide ou expiré.', 'Invalid or expired token.'));
      }
    } catch (err) {
      setError(L('Erreur de connexion au serveur.', 'Server connection error.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 relative overflow-hidden"
      >
        {/* Glow effect */}
        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full bg-pink-500/20 blur-3xl" />

        {/* Request Reset Step */}
        {step === 'request' && (
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/25 to-pink-500/15 border border-primary/30 flex items-center justify-center mb-5">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {L('Mot de passe oublié ?', 'Forgot password?')}
            </h1>
            <p className="text-sm text-zinc-400 mb-6">
              {L('Entrez votre email et nous vous enverrons un lien de réinitialisation.', 'Enter your email and we\'ll send you a reset link.')}
            </p>

            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" /> Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:border-primary focus:outline-none"
                  placeholder="vous@exemple.com"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    {L('Envoyer le lien', 'Send link')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <Link
              to="/espace-client"
              className="mt-4 block text-center text-sm text-zinc-400 hover:text-primary transition-colors"
            >
              ← {L('Retour à la connexion', 'Back to login')}
            </Link>
          </div>
        )}

        {/* Email Sent Confirmation Step */}
        {step === 'sent' && (
          <div className="relative text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/25 to-emerald-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-5">
              <Mail className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">
              {L('Vérifiez votre email 📬', 'Check your inbox 📬')}
            </h1>
            <p className="text-sm text-zinc-300 mb-2 leading-relaxed">
              {L(
                'Si un compte est associé à cet email, vous recevrez un lien de réinitialisation dans quelques minutes.',
                'If an account exists for this email, you will receive a reset link within a few minutes.'
              )}
            </p>
            <p className="text-xs text-zinc-500 mb-6">
              {L(
                'Pensez à vérifier vos spams. Le lien expire dans 1 heure.',
                'Check your spam folder. The link expires in 1 hour.'
              )}
            </p>
            <Link
              to="/espace-client"
              className="inline-block w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform"
              data-testid="reset-back-to-login"
            >
              {L('Retour à la connexion', 'Back to login')}
            </Link>
          </div>
        )}

        {/* Reset Password Step */}
        {step === 'reset' && (
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/25 to-pink-500/15 border border-primary/30 flex items-center justify-center mb-5">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {L('Nouveau mot de passe', 'New password')}
            </h1>
            <p className="text-sm text-zinc-400 mb-6">
              {L('Choisissez un nouveau mot de passe sécurisé.', 'Choose a new secure password.')}
            </p>

            {devToken && (
              <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-400">
                <strong>Mode dev :</strong> Token généré : <code className="bg-black/50 px-1 py-0.5 rounded">{devToken}</code>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" /> {L('Nouveau mot de passe', 'New password')}
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:border-primary focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" /> {L('Confirmer le mot de passe', 'Confirm password')}
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:border-primary focus:outline-none"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    {L('Réinitialiser', 'Reset password')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="relative text-center">
            <div className="w-14 h-14 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mb-5 mx-auto">
              <Check className="w-7 h-7 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {L('Mot de passe réinitialisé !', 'Password reset!')}
            </h1>
            <p className="text-sm text-zinc-400 mb-6">
              {L('Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.', 'You can now log in with your new password.')}
            </p>
            <p className="text-xs text-zinc-500">
              {L('Redirection automatique dans 3 secondes...', 'Automatic redirect in 3 seconds...')}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
